import { t } from "@travily/i18n";
import { TileRadioGroup } from "@ui/components/TileRadioGroup/TileRadioGroup";
import { Wizard } from "@ui/components/Wizard";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import type { ReactNode } from "react";

const COLOR = "green" as const;

export interface ServicePriceTypeStepViewProps {
  /** Current stage, 1-indexed, for the progress bar. */
  currentStage: number;
  /** Total stages driving the progress bar. */
  totalStages: number;
  /** Back handler; omit on the first stage to hide the back affordance. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Questions?", "Finish later"). */
  topBarActions: ReactNode;
  /** Primary "Continue" handler. */
  onPrimary: () => void;
  /** Whether the primary button shows its pending state. */
  primaryPending: boolean;
  /** Currently selected pricing option. */
  priceType: "free" | "paid";
  /** Fires on selection change with the raw radio value. */
  onPriceTypeChange: (value: string) => void;
}

export const ServicePriceTypeStepView = ({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  priceType,
  onPriceTypeChange,
}: ServicePriceTypeStepViewProps) => {
  const isAboveMd = useBreakpoint("md");
  const isAboveLg = useBreakpoint("lg");
  const selectionGroupSize = isAboveLg ? "5xl" : isAboveMd ? "4xl" : "3xl";

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        heading={t("web.services.creator.steps.price_type.show.title")}
        helper={t("web.services.creator.steps.price_type.show.subtitle")}
      >
        <div className="w-full max-w-[600px]">
          <TileRadioGroup
            aria-label={t("web.services.creator.steps.price_type.show.title")}
            align="horizontal"
            color={COLOR}
            size={selectionGroupSize}
            value={priceType}
            onChange={onPriceTypeChange}
          >
            <TileRadioGroup.Item value="free">
              {t("web.services.creator.steps.price_type.show.free_label")}
            </TileRadioGroup.Item>
            <TileRadioGroup.Item value="paid">
              {t("web.services.creator.steps.price_type.show.paid_label")}
            </TileRadioGroup.Item>
          </TileRadioGroup>
        </div>
      </Wizard.Stage>
    </Wizard>
  );
};
